import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import AssistantAvatar from '../AssistantAvatar';
import BetTicketsContainer from '../BetTicketsContainer';
import TypingLoader from '../TypingLoader';
import BotMessageWidget from '../BotMessageWidget';
import { useAssistantActions } from '../../hooks/useLottoAssistantContext';
import { isWidgetIntent } from '../../utils/widgetDisplayHelpers';
import { parseNumberedList } from './parseNumberedList';
import classes from './BotMessage.module.scss';

const formatTimestamp = (timestamp) =>
  new Date(timestamp).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  });

const BotMessage = React.memo(({ message, content: contentProp, withAvatar = true, onScrollToLastMessage }) => {
  const { sendTextMessage } = useAssistantActions();

  const content = message?.content ?? contentProp ?? '';
  const timestamp = message?.timestamp;
  const messageId = message?.id;

  const a2ui = message?.a2ui ?? null;
  const component = a2ui?.components?.[0] ?? null;
  const isBetTickets = component?.type === 'bet_tickets';
  const betTickets = isBetTickets ? component.props.bet_tickets : null;
  const hasBetTickets = !!betTickets && betTickets.length > 0;
  const isWidget = isWidgetIntent(a2ui);
  const isTextMessage = !hasBetTickets && !isWidget;

  const [isWidgetLoading, setIsWidgetLoading] = useState(isWidget);
  const hasLoadedDataRef = useRef(false);

  const handleWidgetLoaded = useCallback(() => {
    hasLoadedDataRef.current = true;
    setIsWidgetLoading(false);
  }, []);

  useEffect(() => {
    if (isWidget && !isWidgetLoading && hasLoadedDataRef.current) {
      onScrollToLastMessage?.(true);
    }
  }, [isWidget, isWidgetLoading, onScrollToLastMessage]);

  const parsedList = useMemo(
    () => (isTextMessage ? parseNumberedList(content) : null),
    [isTextMessage, content]
  );

  const handleListItemClick = useCallback(
    (item) => {
      sendTextMessage(item.fullText);
    },
    [sendTextMessage]
  );

  const containerClass = isTextMessage
    ? `${classes.Container} ${classes.Row}`
    : isWidget
      ? `${classes.Container} ${classes.Column}`
      : `${classes.Container} ${classes.ColumnCompact}`;

  return (
    <div className={containerClass}>
      {withAvatar && !isWidgetLoading && (
        <AssistantAvatar customClass={classes.Avatar} />
      )}

      <div className={classes.MessageWrapper}>
        {hasBetTickets && (
          <BetTicketsContainer tickets={betTickets} messageId={messageId} />
        )}

        {isWidget && (
          <>
            {isWidgetLoading && <TypingLoader />}
            <div
              className={classes.WidgetWrapper}
              style={{ display: isWidgetLoading ? 'none' : 'block' }}
            >
              <BotMessageWidget
                component={component}
                message={content}
                messageId={messageId}
                onWidgetDataLoaded={handleWidgetLoaded}
              />
            </div>
          </>
        )}

        {isTextMessage && (
          <div className={classes.Bubble}>
            {parsedList ? (
              <div className={classes.Text}>
                {parsedList.remainingText && (
                  <p className={classes.RemainingText}>{parsedList.remainingText}</p>
                )}
                <div className={classes.List}>
                  {parsedList.listItems.map((item, index) => (
                    <button
                      key={index}
                      type="button"
                      className={classes.ListItem}
                      onClick={() => handleListItemClick(item)}
                    >
                      <span className={classes.ListItemNumber}>{item.number}.</span>
                      <span className={classes.ListItemText}>{item.text}</span>
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <p className={classes.Text}>{content}</p>
            )}
          </div>
        )}

        {timestamp && !isWidgetLoading && (
          <span
            className={`${classes.Timestamp} ${!isTextMessage ? classes.TimestampInset : ''}`}
          >
            {formatTimestamp(timestamp)}
          </span>
        )}
      </div>
    </div>
  );
});

BotMessage.displayName = 'BotMessage';

export default BotMessage;
